import {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query'
import { Mutex } from 'async-mutex'

import { useAuthStore, useRootStore } from '../store'

export const getBaseQueryWithMockError =
  (mock: any): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> =>
  async (args, api, extraOptions) => {
    const result: any = await getCustomFetch()(args, api, extraOptions)

    if (result.error) {
      delete result.error
      result.data = mock
    }
    return result
  }

// Create a new mutex
const mutex = new Mutex()

const baseUrl =
  import.meta.env.VITE_USER_NODE_ENV === 'development'
    ? import.meta.env.VITE_API_URL
    : '/api/v1'

export const baseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: headers => {
    const { accessToken } = useAuthStore.getState()
    if (accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`)
    }
    return headers
  },
})

async function refreshTokenAndRetry(
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: object,
) {
  if (mutex.isLocked()) {
    await mutex.waitForUnlock()
    return baseQuery(args, api, extraOptions)
  }
  // useAuthStore.getState().clearAccessToken()
  const release = await mutex.acquire()

  try {
    const { refreshToken } = useAuthStore.getState()
    const { profile: user } = useRootStore.getState()

    const refreshResult = await fetch(`${baseUrl}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify({
        refresh_token: refreshToken ?? '',
        id: user?.id,
      }),
    }).then(res => res.json())

    if (refreshResult) {
      const { accessToken, refreshToken: newRefreshToken } = refreshResult

      useAuthStore.setState(state => ({
        ...state,
        accessToken,
        refreshToken: newRefreshToken,
      }))

      return baseQuery(args, api, extraOptions)
    } else {
      useAuthStore.getState().clear()
      return { error: { status: 401, data: 'Refresh token failed' } } // Return an error
    }
  } finally {
    release()
  }
}

export const getCustomFetch = (): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object
> => {
  return async (args, api, extraOptions) => {
    await mutex.waitForUnlock()
    const result = await baseQuery(args, api, extraOptions)

    if (result.error?.status === 401) {
      return refreshTokenAndRetry(args, api, extraOptions)
    }

    return result
  }
}
